<template>
  <vf-dialog-layout class-wrapper="!p-0">
    <template #title>
      {{ $t.quickshop }}
    </template>
    <section v-if="!closeOnAdd" data-scroll-el="notificationSection">
      <vf-notification
        v-if="cart.notification.message"
        class="m-2 mt-3 md:mb-4 md:mt-2"
        :type="cart.notification.type"
        @close="cart.notification.message = ''"
      >
        {{ cart.notification.message }}
      </vf-notification>
    </section>
    <div v-if="productPending || inventoryPending">
      <div v-for="key in 5" :key class="h-12 skeleton b-b b-white" />
    </div>
    <div v-else ref="sizePickersEl" class="mb-20 space-y-6">
      <section
        v-for="({ options, type }) in sizeAttributes"
        :key="type"
        class="relative"
        :data-scroll-el="`size-picker-${type}`"
      >
        <fieldset>
          <legend class="w-full b-b b-grey-70 px-6 py-4 subtitle-4 fw-bold">
            {{ $t.selectAttribute[type === 'size' ? isDigitalProduct ? 'amount' : 'size' : type] }}
          </legend>
          <base-radio
            v-for="({ available, label: sizeLabel, value }, key) in options.map(getVariantOptions)"
            :key
            data-test-id="vf-size-picker"
            :model-value="attributeSelection[type]"
            :name="`${details?.id || product?.id}-${type}`"
            :value="value"
            @pointerdown="enableScroll = true"
            @update:model-value="selectSize(value, sizeLabel, type)"
          >
            <span
              class="h-12 flex cursor-pointer items-center b-b b-grey-80 px-6 between peer-focus-visible:outline-auto"
              :class="{
                'bg-grey-90 c-grey-20': attributeSelection[type] !== value && (!available || isComingSoon),
                'bg-black c-white': attributeSelection[type] === value,
              }"
            >
              <span>{{ sizeLabel }}</span>
              <template v-if="isComingSoon">
                {{ $t.comingSoon }}
              </template>
              <span v-else-if="$feature.showSizeLevelStockMessagingOnPDP && getStockStatus(value)" class="text-xs capitalize">
                {{ getStockStatus(value) }}
              </span>
              <template
                v-else-if="details?.productType !== 'Digital' && !(available && getAttrOptionAvailable(type, value))"
              >
                <span
                  v-if="$feature.showNotifyMeOnQuickshop && getAttrOptionNotifyMe(type, value)"
                  class="text-xs fw-medium"
                >
                  {{ $t.notifyMe }}
                </span>
                <span v-else class="text-xs">{{ $t.outOfStock }}</span>
              </template>
            </span>
          </base-radio>
        </fieldset>
      </section>
    </div>
    <template v-if="selectedVariant" #footer>
      <vf-button v-if="isNotifyMe && (isOutOfStock || isComingSoon)" class="w-full" @click="handleNotifyMe">
        {{ $t.notifyMe }}
      </vf-button>
      <vf-button v-else-if="isComingSoon" class="w-full" disabled>
        {{ $t.comingSoon }}
      </vf-button>
      <vf-button v-else-if="isOutOfStock" class="w-full" disabled>
        {{ $t.outOfStock }}
      </vf-button>
      <vf-button v-else-if="isSignInToBuy" class="w-full" @click="signInToBuy">
        {{ $t.signInToBuy }}
      </vf-button>
      <vf-button v-else-if="context" class="w-full" :loading="loading" @click="updateInCart">
        {{ $t.updateInCart }}
      </vf-button>
      <vf-button v-else class="w-full" :loading="loading" @click="addToCart">
        {{ $t.addToCart }}
      </vf-button>
      <div
        v-if="showExpressPaymentCtasInQuickshop && isProductAvailable"
        class="grid gap-2"
        :class="{
          'cols-2': isApplePayEnabled && isPaypalButtonEnabled,
          'mt-2': isApplePayEnabled || isPaypalButtonEnabled,
        }"
      >
        <checkout-apple-pay-button
          v-if="isApplePayEnabled"
          class="w-full"
          :class="{ 'h-full': isPaypalButtonEnabled }"
          :style="isPaypalButtonEnabled && paypalStyle?.height && `max-height: ${pxToRem(paypalStyle?.height)}`"
          @click="triggerApplePaySession(selectedVariant)"
        />
        <form-paypal-adyen
          v-if="isPaypalButtonEnabled"
          class="w-full"
          context="paypalPdp"
          hide-pay-later-text
          :instance
          is-express-flow
          is-same-as-shipping
          :locale
          :selected-variant
        />
      </div>
    </template>
    <dialog-sign-in />
    <dialog-notify-me size="sm" />
  </vf-dialog-layout>
</template>

<script lang="ts" setup>
import type { GetPaymentSessionData } from '#root/api/clients/cart/data-contracts'
import type { Product, ProductVariants } from '#root/api/clients/product/data-contracts'
import type { CSSClass } from '#types/common'
import type { InteractionOrigin, ProductContext, ShippingMethodContext } from '#types/gtm'
import type { PaypalInstance } from '#types/paypal'
import type { AttributeSelection, BaseOption, VariantWithStockStatus } from '#types/product'
import { ApiErrorCode } from '#root/enums/api'

export type Props = {
  productId?: string
  product?: Product
  closeOnAdd?: boolean
  context?: 'update-cart-item' | 'update-saved-item'
  classProductName?: CSSClass
  initialAttributes?: ProductVariants[number]['attributes']
  updateProductId?: (id: string) => void
  categoryURL?: string
  gtmConfig?: ProductContext & ShippingMethodContext & { appSource?: InteractionOrigin }
  storeId?: string
  title?: string
}
export type Emits = {
  resolve: []
}

const { closeOnAdd, context, gtmConfig, product, storeId } = defineProps<Props>()

const emit = defineEmits<Emits>()

const productId = defineModel<string>('productId')

const { defaultImageUrl, notifyMe: { dialogOptions } } = useAppConfig().components.product
const { name } = useAttrs()
const auth = useAuthStore()
const cart = useCartStore()
const currentDialog = useCurrentDialog<Props, Emits>()
const { DialogSignIn } = useDialogsStore()
const { $feature, $gtm, $locale, $sendExtraMonetateEvents, $t } = useNuxtApp()
const {
  allowApplePayOrderOnPDP,
  allowPayPalOrderOnCart,
  showExpressPaymentCtasInQuickshop
} = useExpressPaymentFeatures()
const route = useRoute()
const toast = useToaster()
const gtmTrace = useSessionStorage('gtmTrace', {} as Record<string, ShippingMethodContext>)
const { trackEvent } = useOptimizely()
const paypalStyle = useAppConfig().components.paypal.button.style
const locale = computed(() => cart.paypalPaymentSession.locale)
const isAdyenEnabled = ref(false)
const paymentProvider = ref<GetPaymentSessionData['provider']>()
const instance = ref<PaypalInstance>()
const enableScroll = ref<boolean>(false)

const DialogNotifyMe = createDialog('notify-me', dialogOptions)

const activeProduct = ref(productId.value || product?.id || '')
const loading = ref(false)
const parentInteractionOrigin = ref(getInteractionOrigin())
const sizePickersEl = ref<HTMLElement>()

const { productPending, product: details, inventory, inventoryPending } = await useProduct(activeProduct, {
  server: false,
  // @ts-expect-error incorrect Nuxt types
  default: () => product,
  transform: (input) => ({
    ...input,
    badge: input.badge || product?.badge,
    eyebrow: input.eyebrow || product?.eyebrow
  }),
  key: `quickshop-${activeProduct.value}`,
  onResponse: async ({ response: { _data } }) => productId.value = _data.id
})

const attributeSelection = reactive<AttributeSelection>({})
const attributeSelectionLabels = useAttributeSelectionLabels(details, attributeSelection)
const sizeAttributes = computed(() => details.value?.attributes.filter((attr) => attr.type !== 'color') || [])
const { getAttrOptionAvailable, getAttrOptionNotifyMe } = getProductAvailability(attributeSelection, details)

const isRequiredAttributeSelection = computed(
  () => Object.keys(attributeSelection).length >= 1
)
const isVariantPicked = computed(() =>
  isRequiredAttributeSelection.value && details.value?.attributes.every(({ type }) => attributeSelection[type] || type === 'color'))
const selectedVariant = computed(() => {
  const variant = details.value?.variants.find((item) =>
    Object.entries(attributeSelection).every(([key, value]) => !item.attributes[key] || item.attributes[key] === value || key === 'color')
  )
  return variant && isVariantPicked.value ? variant : undefined
})
const nextAttribute = computed(() => {
  const index = Object.entries(attributeSelectionLabels.value).findIndex(
    ([type, { value }]) => type !== 'color' && !value
  )

  return details.value?.attributes[index]
})

const getVariantOptions = (attribute: BaseOption) => {
  const variant = details.value?.variants.find((variant) => variant.attributes.size === attribute.value)
  return {
    ...attribute,
    ...(variant || {}),
    ...(inventory.value?.variants?.[variant?.id || ''] || {}),
    ...(isCustomsProduct(details) && { inStock: true, quantity: 10, stockStatus: 'AVAILABLE' })
  }
}
const isDigitalProduct = computed(() => details.value?.productType?.toLowerCase() === 'digital')
const isNotifyMe = computed(() => $feature.showNotifyMeOnQuickshop && selectedVariant.value?.notifyMe)
const isOutOfStock = computed(() => selectedVariant.value?.productInventoryState !== 'InStock')
const isSignInToBuy = computed(() => details.value?.presale && !auth.loggedIn)
const isComingSoon = computed(() => $feature.showComingSoonOnQuickshop && getIsComingSoonProduct(details.value))
const isProductAvailable = computed(() =>
  !isSignInToBuy.value && !isOutOfStock.value && !isComingSoon.value && !(isNotifyMe.value && isOutOfStock.value)
)

const url = computed(() => details.value?.url || product?.url)

const fetchCustomsData = async (customsRecipeID, size, locale) => {
  try {
    const [{ sku }, imageUrl] = await Promise.all([
      $fetch(`/fapi/customs-sku/${customsRecipeID}`, { query: { locale, size } }),
      $fetch(`/fapi/customs-image/${customsRecipeID}`, {
        query: { locale },
        default: () => defaultImageUrl
      })
    ])
    return { productId: sku, customsProductImageURL: imageUrl }
  }
  catch {
    toast.add({
      props: {
        type: 'error',
        title: $t.somethingWentWrong,
        message: $t.itemCannotAddedToCart
      }
    })
    return { productId: undefined, customsProductImageURL: undefined }
  }
}

async function addToCart() {
  if (!details.value) return
  if (!isProductAvailable.value) return
  loading.value = true
  cart.notification.message = ''

  const customsRecipeID = details.value.customsRecipeID
  const recipeId = isCustomsProduct(details) ? customsRecipeID : undefined
  const precreatedCustomsCode = details.value.dummyCustoms ? details.value.id : undefined
  const { productId, customsProductImageURL } = customsRecipeID && details.value.dummyCustoms
    ? await fetchCustomsData(customsRecipeID, selectedVariant.value?.attributes.size, $locale)
    : { productId: selectedVariant.value?.id, customsProductImageURL: undefined }

  if (!productId) {
    loading.value = false
    return
  }

  setGtmCartProductsMap(productId, details.value)
  if (gtmConfig?.category) {
    const stock = inventory.value?.variants[selectedVariant.value?.id || productId]
    setGtmCartProductVariant(
      productId,
      {
        category: gtmConfig.category,
        stockStatus: stock?.stockStatus,
        stockQuantity: stock?.quantity
      }
    )
  }

  try {
    const { shippingMethod, shippingStoreId, shippingWindow } = gtmConfig || {}

    if (shippingMethod && shippingStoreId && shippingWindow)
      gtmTrace.value[productId] = { shippingMethod, shippingStoreId, shippingWindow }

    await withRetryAfterTime(() => cart.add({
      product: productId,
      upc: selectedVariant.value?.upc,
      suppressMiniCart: ['shopTheLook', 'minicart'].includes(name as string),
      suppressNotification: name === 'shopTheLook',
      maxQty: 1,
      storeId: shippingStoreId,
      extendedGtmConfig: {
        ...gtmConfig,
        category: gtmConfig?.category || interactionOriginMap[gtmConfig?.appSource!]
      },
      productImageURL: customsProductImageURL,
      recipeId,
      precreatedCustomsCode
    }), 2000, [ApiErrorCode.GWP_PRODUCT])
    $sendExtraMonetateEvents()
    trackEvent('quick_shop', {
      product: details.value?.id || activeProduct.value
    })
  }
  catch (err) {
    log.error(err)
    delete gtmTrace.value[productId]
  }

  loading.value = false

  if (cart.error) {
    cart.setNotification('error', cart.errorMessage)
    removeGtmCartProductsMap(productId, details.value.id)
    return
  }

  if ($feature.showMiniCartOnAddProduct || closeOnAdd)
    emit('resolve')
}

const updateInCart = async () => {
  loading.value = true

  try {
    if (context === 'update-cart-item') {
      const { id = '', itemId = '' } = product || {}
      const currentItem = itemId && cart.items.find(({ id }) => id === itemId)

      if (selectedVariant.value?.id)
        setGtmCartProductsMap(selectedVariant.value?.id, { ...details.value, list: gtmConfig?.list })

      if (currentItem)
        setGtmCartProductVariant(selectedVariant.value?.id!, getGTMProduct(currentItem.productId))

      await cart.updateItemInCart(
        itemId,
        id,
        activeProduct.value,
        selectedVariant.value?.id!,
        selectedVariant.value?.upc,
        undefined,
        undefined,
        storeId
      )
    }

    if (context === 'update-saved-item')
      await cart.updateSavedItemInCart(product!.itemId as string, selectedVariant.value?.id as string, url.value)

    await toast.add({
      props: {
        title: $t.success,
        message: replaceAll($t.cartItemEdited, { name: details.value?.name }),
        type: 'success',
      },
      promise: true
    })
  }
  catch (err) {
    log.error(err)
    delete gtmTrace.value[selectedVariant.value?.id!]
  }

  loading.value = false

  if (cart.error) {
    cart.setNotification('error', cart.errorMessage)
    if (selectedVariant.value?.id && details.value)
      removeGtmCartProductsMap(selectedVariant.value?.id, details.value.id)
  }

  if ($feature.showMiniCartOnAddProduct || closeOnAdd)
    emit('resolve')
}

const selectSize = (value: string, label: string, type: string) => {
  attributeSelection[type] = value

  const eventLabel = `${activeProduct.value} - ${label}`

  if (name === 'shopTheLook')
    $gtm.push('shopTheLook.onSelectSize', eventLabel, { bundleId: gtmConfig!.bundleId! })
  else
    $gtm.push('plpPage.onSelectSize', eventLabel)

  if (enableScroll.value && nextAttribute.value) {
    scrollToElement(`size-picker-${nextAttribute.value.type}`)
    enableScroll.value = false
  }
}

const handleNotifyMe = () => {
  const setNotification = ({ message, type }) => toast.add({
    props: {
      type,
      message
    }
  })

  const productColor = useProductColor(details)
  const size = attributeSelectionLabels.value.size?.value || attributeSelection.size
  const length = attributeSelection.length
  const width = attributeSelection.width

  DialogNotifyMe.keep().open({
    productId: selectedVariant.value?.id!,
    selectedAttributes: attributeSelectionLabels.value,
    gtmEventLabel: `${details.value?.id} - ${productColor.value?.label}: ${[size, length, width].filter(Boolean).join('/')}`,
    setNotification,
    subscriptionType: details.value?.subscriptionType,
    productName: details.value?.name || '',
    onNotifySignUp: () => emit('resolve')
  })

  $gtm.push('pdpPage.onNotifyMeOpen', activeProduct.value)
}

const signInToBuy = async () => {
  await DialogSignIn.keep().open({
    formLocation: 'modal:single:quickshop:none',
    isSignInToBuy: true
  })
  if (auth.loggedIn) {
    await until(() => cart.pending).toBe(false)
    addToCart()
  }
}

const getStockStatus = (optionValue: string) => {
  if (!details.value) return
  const variant = details.value?.variants.find((variant) =>
    variant.attributes.size === optionValue) as VariantWithStockStatus

  if (variant?.stockStatusLabel)
    return variant.stockStatusLabel.toLowerCase()

  return null
}

const pushProductDetailViewEvent = (product) => {
  $gtm.push('product.onProductDetailView', product, {
    viewType: 'Quick Shop',
    breadcrumbs: deserializeAnalyticsBreadcrumbs(history.state.breadcrumbs),
    bundleId: gtmConfig?.bundleId,
    category: interactionOriginMap[gtmConfig?.appSource!] || gtmConfig?.category || history.state.category,
    searchTerm: route.query.q?.toString(),
    stockStatus: inventory.value?.variants[product?.id!]?.stockStatus
  })
}

const isPaypalButtonEnabled = computed(() =>
  showExpressPaymentCtasInQuickshop.value
  && allowPayPalOrderOnCart.value
  && isAdyenEnabled.value
  && !auth.inStoreEmployeeLoggedIn
)

const isApplePayEnabled = computed(() =>
  showExpressPaymentCtasInQuickshop.value
  && allowApplePayOrderOnPDP.value
  && window.ApplePaySession
)

const triggerApplePaySession = (selectedVariant) => {
  try {
    const payload = { productId: selectedVariant.id, upc: selectedVariant.upc }

    $gtm.push('checkout.onLoadEventData', 'APPLE_PAY')

    // initiate the temporary applePay only after clicking the button
    const { initApplePaySession } = useApplePay('applePayPdp')
    initApplePaySession(payload)
  }
  catch (e) {
    log.info(e)
  }
}

const getPaymentSession = async () => {
  try {
    const session = await cart.ensurePaypalPaymentSession()

    if (!session.success)
      throw session

    paymentProvider.value = session.provider
    if (
      showExpressPaymentCtasInQuickshop.value
      && allowPayPalOrderOnCart.value
      && cart.paypalPaymentSession.locale && cart.paypalPaymentSession.meta
    ) {
      instance.value = await cart.loadPaypal(cart.paypalPaymentSession.locale, cart.paypalPaymentSession.meta)
    }
  }
  catch (e) {
    log.error(e)
  }
}

// Once ADYEN is the ONLY provider this condition could be removed
watch(() => paymentProvider.value, () => {
  if (paymentProvider.value === 'ADYEN')
    isAdyenEnabled.value = true
})

// Push updated event with inventory data once it becomes available
watch(inventory, (newInventory) => {
  if (newInventory && details.value)
    pushProductDetailViewEvent(details.value)
}, { immediate: false })

onMounted(async () => {
  // get payment provider
  getPaymentSession()

  if (currentDialog.reopened) {
    await until(sizePickersEl).toBeTruthy()
    addToCart()
  }
  cart.clearNotification()

  if (interactionOriginMap[gtmConfig?.appSource!])
    setInteractionOrigin(gtmConfig!.appSource, true)
  else
    setInteractionOrigin()
})

onUnmounted(() => {
  if (!closeOnAdd)
    cart.clearNotification()

  if (interactionOriginMap[gtmConfig?.appSource!])
    setInteractionOrigin(parentInteractionOrigin.value || '')
})

if (!closeOnAdd) {
  watch(cart.notification, () => {
    if (cart.notification.message)
      scrollToElement('notificationSection')
  })
}
</script>
